import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format.js';
import type { QueuedWrite } from '../../lib/offline/db.js';
import { useOffline } from '../../lib/offline/offline-context.js';
import {
  Badge,
  Button,
  useConfirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tailoring-crm/ui';

/**
 * Everything the queue is holding, and what went wrong with the ones that were refused.
 *
 * Nothing here is decided for the owner. A rejected write is shown with the server's reason —
 * a conflict is called a conflict, "someone else changed this first" — and they choose to send
 * it again or drop it. Silently discarding a shop's work, or silently overwriting someone
 * else's, are both worse than asking.
 */
export function QueuedWritesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { pending, failed, retry, discard } = useOffline();
  const confirm = useConfirm();

  const handleDiscard = async (write: QueuedWrite) => {
    const ok = await confirm({
      title: t('offline.queue.discardTitle'),
      description: t('offline.queue.discardDescription'),
      confirmLabel: t('offline.queue.discardConfirm'),
      destructive: true,
    });
    if (ok) await discard(write.seq);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('offline.queue.title')}</DialogTitle>
          <DialogDescription>{t('offline.queue.description')}</DialogDescription>
        </DialogHeader>

        {failed.length === 0 && pending.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('offline.queue.empty')}
          </p>
        ) : null}

        {failed.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">{t('offline.queue.failedHeading')}</h4>
            <ul className="flex flex-col gap-2">
              {failed.map((write) => (
                <li
                  key={write.id}
                  className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium">
                      {t(write.labelKey, write.labelParams)}
                    </span>
                    <Badge variant={write.lastError?.conflict ? 'warning' : 'destructive'}>
                      {write.lastError?.conflict
                        ? t('offline.queue.conflict')
                        : t('offline.queue.rejected')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {write.lastError?.message ?? t('offline.queue.unknownReason')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('offline.queue.savedAt', { at: formatDateTime(write.createdAt) })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        void retry(write.seq);
                      }}
                    >
                      {t('offline.queue.retry')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        void handleDiscard(write);
                      }}
                    >
                      {t('offline.queue.discard')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {pending.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold">{t('offline.queue.pendingHeading')}</h4>
            <ol className="flex flex-col gap-2">
              {pending.map((write, index) => (
                <li
                  key={write.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border p-3"
                >
                  <span className="text-sm">
                    {t('offline.queue.position', { position: index + 1 })}{' '}
                    {t(write.labelKey, write.labelParams)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(write.createdAt)}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
